import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import type { CompanyContact } from "../../api/client.ts";
import { getInitials } from "../UserAvatar.tsx";
import { titleCase } from "../../utils/format.ts";

interface BoardGraphProps {
  contacts: CompanyContact[];
  companyName: string;
}

const ROLE_ORDER: Record<string, number> = {
  CHAIR_OF_BOARD: 0,
  MANAGING_DIRECTOR: 1,
  DEPUTY_CHAIR: 2,
  BOARD_MEMBER: 3,
  DEPUTY_BOARD_MEMBER: 4,
  AUDITOR: 5,
  ACCOUNTANT: 6,
  SOLE_PROPRIETOR: 7,
  BUSINESS_MANAGER: 8,
  CONTACT_PERSON: 9,
};

function roleOrder(role: string): number {
  return ROLE_ORDER[role] ?? 99;
}

function formatRole(role: string, titleEn: string | null): string {
  if (titleEn) return titleEn;
  return titleCase(role);
}

export default function BoardGraph({ contacts }: BoardGraphProps) {
  const active = contacts.filter((c) => c.is_active);
  const inactive = contacts.filter((c) => !c.is_active);

  const grouped = new Map<string, CompanyContact[]>();
  for (const c of [...active, ...inactive]) {
    const list = grouped.get(c.role_type) ?? [];
    list.push(c);
    grouped.set(c.role_type, list);
  }

  const sortedGroups = [...grouped.entries()].sort(([a], [b]) => roleOrder(a) - roleOrder(b));

  return (
    <Box>
      {sortedGroups.map(([roleType, members], gi) => (
        <Box key={roleType}>
          {gi > 0 && <Divider />}
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", px: 2, pt: gi === 0 ? 0 : 1, display: "block" }}
          >
            {formatRole(roleType, members[0]?.role_title_en ?? null)}
          </Typography>
          <List dense disablePadding>
            {members.map((ct) => (
              <ListItem key={`${ct.contact_id}-${ct.role_type}`} sx={{ opacity: ct.is_active ? 1 : 0.45 }}>
                <ListItemAvatar>
                  <Avatar sx={{ width: 32, height: 32, fontSize: "0.75rem" }}>
                    {getInitials(ct.full_name)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ct.full_name}
                    </Typography>
                  }
                />
                {!ct.is_active && (
                  <Chip label="Departed" size="small" sx={{ ml: 1 }} />
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}
